import * as React from "react"
import useAllCallbacks from "../useAllCallbacks"
import { TestIdProp, testIdProp } from "@oui/system/testing"
import {
  isFocusableElement,
  isFormElement,
  isInteractiveElement,
  hasEventKey
} from "./utils"
import { omit } from "ramda"
import useActionableSet from "../ActionableSet/useActionableSet"
import useMergeRefs from "../useMergeRefs"

type DefaultProps = typeof defaultProps
type BaseProps = Pick<React.HTMLAttributes<any>, "children"> &
  React.RefAttributes<any>
type Props = Readonly<
  BaseProps & {
    /**
     * Blur focus event handler.
     */
    onBlur?: React.FocusEventHandler
    /**
     * Change event handler.
     */
    onChange?: React.ChangeEventHandler
    /**
     * Click event handler.
     */
    onClick?: React.MouseEventHandler
    /**
     * Focus event handler.
     */
    onFocus?: React.FocusEventHandler
    /**
     * KeyDown event handler.
     */
    onKeyDown?: React.KeyboardEventHandler
    /**
     * MouseDown event handler.
     */
    onMouseDown?: React.MouseEventHandler
    /**
     * MouseOver event handler.
     */
    onMouseOver?: React.MouseEventHandler
    /**
     * ARIA defines "role" semantics which can be applied to the actionable.
     */
    role?: string
  } & TestIdProp &
    DefaultProps
>

const defaultProps = {
  /**
   * List of keyboard keys that trigger click events.
   * @default ["Enter", " "]
   */
  clickKeys: ["Enter", " "],
  /**
   * Sets the element's `aria-disabled` attribute.
   * @default false
   */
  disabled: false,
  /**
   * Sets the underlying DOM element tag.
   * @default "button"
   */
  from: "button" as keyof JSX.IntrinsicElements | React.ComponentType<any>,
  /**
   * Sets whether the element can receive tab focus.
   */
  focusable: true
}

const Actionable = ({
  clickKeys,
  disabled,
  from: Component,
  focusable,
  forwardedRef,
  onClick,
  onKeyDown,
  onMouseDown,
  onMouseOver,
  onFocus,
  onBlur,
  ...rest
}: Props & { forwardedRef: React.Ref<HTMLElement> }) => {
  const completelyDisabled = disabled && !focusable

  const localRef = React.useRef<HTMLElement>()
  const ref = useMergeRefs(localRef, forwardedRef)

  const { rovingElement: rovingEl, setRovingElement } = useActionableSet()
  const { rovingElement } = React.useMemo(
    () => ({
      rovingElement: rovingEl
    }),
    [rovingEl]
  )

  const handleOnClick = React.useCallback<React.MouseEventHandler>(
    event => {
      if (disabled || !focusable) {
        event.stopPropagation()
        event.preventDefault()
        return
      }
      onClick?.(event)
    },
    [disabled, focusable, onClick]
  )

  const handleOnKeyDown = React.useCallback<React.KeyboardEventHandler>(
    event => {
      if (
        disabled ||
        !hasEventKey(clickKeys, event) ||
        (hasEventKey(defaultProps.clickKeys, event) &&
          isInteractiveElement(event.target as HTMLElement))
      ) {
        return
      }

      event.preventDefault()
      const clickEvent = new MouseEvent("click", {
        bubbles: true,
        view: window
      })
      event.target.dispatchEvent(clickEvent)
    },
    [clickKeys, disabled]
  )

  const handleOnMouseDown = React.useCallback<React.MouseEventHandler>(
    event => {
      const target = event.target as HTMLElement
      if (
        isFormElement(target) ||
        // https://github.com/facebook/react/issues/11387
        !event.currentTarget.contains(target)
      ) {
        onMouseDown?.(event)
        return
      }
      event.preventDefault()
      if (disabled || !focusable) {
        event.stopPropagation()
        return
      }
      const currentTarget = event.currentTarget as HTMLElement
      if (currentTarget === target || !isFocusableElement(target)) {
        currentTarget.focus({ preventScroll: true })
      }
      onMouseDown?.(event)
    },
    [disabled, focusable, onMouseDown]
  )

  const handleOnMouseOver = React.useCallback<React.MouseEventHandler>(
    event => {
      if (disabled) {
        event.stopPropagation()
        event.preventDefault()
        return
      }
      onMouseOver?.(event)
    },
    [disabled, onMouseOver]
  )

  const handleOnFocus = React.useCallback<React.FocusEventHandler>(
    event => {
      if (!focusable) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      if (localRef.current !== rovingElement) {
        setRovingElement(localRef.current)
      }
      onFocus?.(event)
    },
    [focusable, localRef, onFocus, rovingElement, setRovingElement]
  )

  const handleOnBlur = React.useCallback<React.FocusEventHandler>(
    event => {
      if (!focusable) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      onBlur?.(event)
    },
    [focusable, onBlur]
  )

  const calcTabIndexWithinSet = React.useCallback(() => {
    const isRovingElement = localRef.current === rovingElement
    return !focusable ? -2 : isRovingElement ? 0 : -1
  }, [focusable, rovingElement])

  const calcTabIndexWithoutSet = React.useCallback(
    () => (!focusable ? -1 : 0),
    [focusable]
  )

  const calcTabIndex = React.useCallback(
    () => (rovingElement ? calcTabIndexWithinSet() : calcTabIndexWithoutSet()),
    [calcTabIndexWithinSet, calcTabIndexWithoutSet, rovingElement]
  )
  const [tabIndex, setTabIndex] = React.useState(calcTabIndex())

  React.useLayoutEffect(() => {
    setTabIndex(calcTabIndex())
  }, [calcTabIndex])

  const props = {
    ref,
    tabIndex,
    "aria-disabled": disabled || undefined,
    disabled: completelyDisabled,
    onClick: handleOnClick,
    onKeyDown: useAllCallbacks(onKeyDown, handleOnKeyDown),
    onMouseDown: handleOnMouseDown,
    onMouseOver: handleOnMouseOver,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    ...testIdProp(rest),
    ...omit(["testId"], rest)
  }
  return <Component {...props} />
}
Actionable.defaultProps = defaultProps

const ForwardedActionable_ = (
  props: Omit<Props, keyof DefaultProps> & Partial<DefaultProps>,
  ref: React.Ref<HTMLElement>
) => <Actionable {...props} forwardedRef={ref} />
ForwardedActionable_.displayName = "forwarded(Actionable)"

const ForwardedActionable = React.forwardRef(ForwardedActionable_)

export { Actionable as _PropsComponent_ }
export default ForwardedActionable
